import { BaseRestRepository } from '~/repositories/baseRestRepository';
import type { PropertyManagerDto, UpdatePropertyManagerDto } from '~/swagger/portal/Api';
export const PROPERTY_MANAGERS_URL = '/api/property-managers';

export class PropertyManagersRepository extends BaseRestRepository {
  public async getPropertyManagers(): Promise<PropertyManagerDto[]> {
    const url = PROPERTY_MANAGERS_URL;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createPropertyManager(propertyManager: PropertyManagerDto): Promise<PropertyManagerDto> {
    const url = PROPERTY_MANAGERS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(propertyManager),
    });
    return this.getJsonResponse(response);
  }

  public async updatePropertyManager(id: string, propertyManager: UpdatePropertyManagerDto): Promise<PropertyManagerDto> {
    const url = `${PROPERTY_MANAGERS_URL}/${id}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(propertyManager),
    });
    return this.getJsonResponse(response);
  }

  public async deletePropertyManager(id: string): Promise<void> {
    const url = `${PROPERTY_MANAGERS_URL}/${id}`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });
    return this.getJsonResponse(response);
  }

  public async getPropertyManagerById(id: string): Promise<PropertyManagerDto> {
    const url = `${PROPERTY_MANAGERS_URL}/${id}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async changePropertyManagerPassword(id: string, currentPassword: string, newPassword: string): Promise<void> {
    const url = `${PROPERTY_MANAGERS_URL}/${id}/password`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ currentPassword: currentPassword, clearTextPassword: newPassword }),
    });
    return this.getJsonResponse(response);
  }

  public async changePropertyManagerActiveStatus(id: string, active: boolean): Promise<void> {
    const url = `${PROPERTY_MANAGERS_URL}/${id}/active`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ active }),
    });
    return this.getJsonResponse(response);
  }

  public async getPropertyManagerPics(id: string): Promise<any> {
    const url = `${PROPERTY_MANAGERS_URL}/${id}/image.webp`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }
}

// Shared, session-cached "all property managers" list. Work-order / maintenance-
// request / runbook lists resolve a row's PM from this instead of each list
// re-fetching every property manager.
const propertyManagersRepositoryInstance = new PropertyManagersRepository();
let allPropertyManagersCache: Promise<PropertyManagerDto[]> | undefined;

export const getCachedPropertyManagers = (): Promise<PropertyManagerDto[]> => {
  if (!allPropertyManagersCache) {
    allPropertyManagersCache = propertyManagersRepositoryInstance.getPropertyManagers();
  }
  return allPropertyManagersCache;
};

export const invalidatePropertyManagersCache = (): void => {
  allPropertyManagersCache = undefined;
};
